import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTenant } from '../../../../contexts/TenantContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { usePreferences } from '../../../../hooks/usePreferences';
import { FeatureFlags } from '../../../../flags';
import { PermissionKey } from '../../../../system/permissions';
import { PreferenceKey } from '../../../../hooks/usePreferences';
import {
  MediaCenterConfig,
  MediaType,
  ResourceFields,
  type TestIdGenerator,
} from '@brightmls/nextgen.lae.mediacenter';
import { useMediaConstraints } from './useMediaConstraints';
import type { MediaV2Config } from './types';

/**
 * Normalizes a media category to match the old media flow test ID conventions.
 * 'Unbranded Virtual Tour' → 'virtualTour'
 * 'Floor Plan' → 'floorplan' (no space)
 * Everything else → lowercase
 */
function normalizeCategory(category: string): string {
  if (!category) return '';
  const lower = category.toLowerCase();
  if (lower === 'unbranded virtual tour') return 'virtualTour';
  // Remove spaces for multi-word categories (e.g. 'Floor Plan' → 'floorplan')
  return lower.replace(/\s+/g, '');
}

/**
 * Test ID generator matching the old media flow conventions.
 * Produces IDs like: photo-1-editButton, document-0-Permission type-selectField
 * Static IDs (no category context) return just the element name.
 */
const generateTestId: TestIdGenerator = (
  category: string,
  displayOrder: number,
  element: string,
): string => {
  // Static test IDs (no category/order context)
  if (!category && displayOrder === -1) {
    return element;
  }
  const normalized = normalizeCategory(category);
  return `${normalized}-${displayOrder}-${element}`;
};

export function useMediaCenterConfig(
  listingKey: string,
  resource: ResourceFields,
): MediaCenterConfig {
  const { allowed } = useAuth();
  const { supportsClientResoWebApi, getConfigValue, isAddEditHost, tenant } =
    useTenant();
  const { hasFeature, getFeatureValue } = useFeatureFlags();
  const { usePreference } = usePreferences();
  const [searchParams] = useSearchParams();
  const constraints = useMediaConstraints();

  const prefix = isAddEditHost() ? `/${tenant}` : '';

  const isResoSystem = supportsClientResoWebApi();
  const isDebugMode = searchParams.has('debug');

  // Get MediaV2 configuration from feature flag
  const mediaV2Config = getFeatureValue(FeatureFlags.NGW_MEDIA_V2) as
    | MediaV2Config
    | undefined;

  // Permissions
  const permissions = {
    photos: {
      add: allowed({ key: PermissionKey.UPLOAD_LISTING_PHOTOS }),
      edit: allowed({ key: PermissionKey.UPLOAD_LISTING_PHOTOS }),
      delete: allowed({ key: PermissionKey.UPLOAD_LISTING_PHOTOS }),
      reorder: allowed({ key: PermissionKey.UPLOAD_LISTING_PHOTOS }),
    },
    documents: {
      add: allowed({ key: PermissionKey.UPLOAD_DOCUMENTS }),
      edit: allowed({ key: PermissionKey.UPLOAD_DOCUMENTS }),
      delete: allowed({ key: PermissionKey.UPLOAD_DOCUMENTS }),
      reorder: allowed({ key: PermissionKey.UPLOAD_DOCUMENTS }),
    },
    tours: {
      add: allowed({ key: PermissionKey.ADD_EDIT_VIRTUAL_TOURS }),
      edit: allowed({ key: PermissionKey.ADD_EDIT_VIRTUAL_TOURS }),
      delete: allowed({ key: PermissionKey.ADD_EDIT_VIRTUAL_TOURS }),
      reorder: allowed({ key: PermissionKey.ADD_EDIT_VIRTUAL_TOURS }),
    },
  };

  // Feature flags
  const features = {
    aiDescriptions: hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS),
    aiDescriptionsOptIn: hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS),
    cloudUploaders: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_UPLOADS),
    objectDetection: hasFeature(FeatureFlags.NGW_OBJECT_DETECTION),
    photoEditor: hasFeature(FeatureFlags.NGW_EDIT_IMAGE),
    bulkEdit: hasFeature(FeatureFlags.NGW_MULTI_DRAG_DROP),
    termsOfService: hasFeature(FeatureFlags.NGW_MEDIA_TERMS),
    autoHideOffMarket: hasFeature(FeatureFlags.NGW_HIDE_MEDIA_OFF_MARKET),
    documentFloorPlans: isResoSystem,
    documentVisibility: isResoSystem,
    documentTitleDescription: isResoSystem,
  };

  // Preferences
  const [showDescriptions, setShowDescriptions] = usePreference<boolean>(
    PreferenceKey.SHOW_DESCRIPTIONS,
    true,
  );
  const [aiDescriptionsEnabled, setAIDescriptionsEnabled] =
    usePreference<boolean>(PreferenceKey.AI_DESCRIPTIONS_ENABLED, true);

  // Cloud providers
  const cloudProviders = features.cloudUploaders
    ? {
        googleDrive: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_GOOGLE)
          ? {
              clientId: getConfigValue(
                'MEDIA_PROVIDER_GOOGLE_DRIVE_CLIENT_ID',
                '',
              ),
              apiKey: getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_API_KEY', ''),
              appId: getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_APP_ID', ''),
            }
          : undefined,
        dropbox: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_DROPBOX)
          ? {
              appId: getConfigValue('MEDIA_PROVIDER_DROPBOX_APP_KEY', ''),
            }
          : undefined,
        oneDrive: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_ONEDRIVE)
          ? {
              appId: getConfigValue('MEDIA_PROVIDER_ONEDRIVE_APP_ID', ''),
            }
          : undefined,
      }
    : undefined;

  return useMemo(
    () => ({
      enabledTypes: constraints.getEnabledTypes(),
      defaultType: mediaV2Config?.defaultType ?? MediaType.Photo,
      categoryMapping: constraints.getCategoryMapping(),
      routing: {
        basePath: `${prefix}/edit/${listingKey}/media`,
        paths: {
          photos: 'photos',
          documents: 'documents',
          tours: 'tours',
        },
      },
      resource,
      features,
      preferences: {
        showDescriptions: [showDescriptions, setShowDescriptions],
        aiDescriptionsEnabled: [
          aiDescriptionsEnabled,
          setAIDescriptionsEnabled,
        ],
      },
      limits: constraints.limits,
      permissions,
      cloudProviders,
      descriptionLimits: {
        maxShortLength: mediaV2Config?.descriptionLimits?.maxShortLength ?? 50,
        maxLongLength: mediaV2Config?.descriptionLimits?.maxLongLength ?? 4000,
        maxUrlLength: mediaV2Config?.descriptionLimits?.maxUrlLength ?? 3000,
      },
      displayOrder: {
        isZeroBased: mediaV2Config?.isZeroBased ?? false,
      },
      // Edit configuration - determines how media replacement is handled
      // RESO systems support direct binary replacement (keeps same media key)
      // OcuSell and others use switch-a-roo (upload new, copy metadata, delete old)
      editConfig: {
        uploadMode: isResoSystem ? 'replace_binary' : 'switch_a_roo',
      },
      debug: isDebugMode,
      generateTestId,
    }),
    [
      isDebugMode,
      listingKey,
      isResoSystem,
      resource,
      features.aiDescriptions,
      features.aiDescriptionsOptIn,
      features.cloudUploaders,
      features.objectDetection,
      features.photoEditor,
      features.bulkEdit,
      features.termsOfService,
      features.autoHideOffMarket,
      permissions.photos.add,
      permissions.documents.add,
      permissions.tours.add,
      cloudProviders,
      showDescriptions,
      setShowDescriptions,
      aiDescriptionsEnabled,
      setAIDescriptionsEnabled,
      constraints,
      mediaV2Config,
    ],
  );
}
